import React, { useState, useEffect, useContext } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { fs } from '../firebase';
import { LocalizationContext } from '../localization/LocalizationContext';
import AddressSheet from './AddressSheet';
import '../css/AddressManager.css';

const LABEL_KEYS = ['labelHome', 'labelWork', 'labelCottage', 'labelOther'];

// backward-compat: old entries stored Georgian text
const GEO_TO_KEY = { 'სახლი': 'labelHome', 'სამსახური': 'labelWork', 'აგარაკი': 'labelCottage', 'სხვა': 'labelOther' };
const normKey = (v) => (v && v.startsWith('label') ? v : GEO_TO_KEY[v] || 'labelOther');

const LABEL_ICONS = {
    labelHome: (
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
            <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
            <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
        </svg>
    ),
    labelWork: (
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21l18 0" />
            <path d="M5 21v-14a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v14" />
            <path d="M9 9l0 .01" />
            <path d="M15 9l0 .01" />
            <path d="M9 13l0 .01" />
            <path d="M15 13l0 .01" />
            <path d="M9 17l0 .01" />
            <path d="M15 17l0 .01" />
        </svg>
    ),
    labelCottage: (
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
            <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />
        </svg>
    ),
    labelOther: (
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
            <path d="M12 12m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
            <path d="M8 12l0 .01" />
            <path d="M16 12l0 .01" />
        </svg>
    ),
};

const LABEL_COLORS = {
    labelHome: '#e8603c',
    labelWork: '#6b7280',
    labelCottage: '#6b7280',
    labelOther: '#6b7280',
};

const roundRedIcon = L.divIcon({
    className: '',
    html: '<div style="width:20px;height:20px;border-radius:50%;background:rgba(232,96,60,0.5);border:3px solid #e8603c;box-shadow:0 0 0 5px rgba(232,96,60,0.18);"></div>',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
});

const MapFixer = () => {
    const map = useMap();
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 200);
        return () => clearTimeout(t);
    }, [map]);
    return null;
};

const AddressManager = ({ onClose }) => {
    const { translations } = useContext(LocalizationContext);
    const am = translations?.addressManager || {};
    const t = (key) => am[key] || key;

    const [addresses, setAddresses] = useState([]);
    const [showAddressSheet, setShowAddressSheet] = useState(false);
    const [labelModal, setLabelModal] = useState(null);
    const [pendingAddress, setPendingAddress] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [gpsAddress, setGpsAddress] = useState(null);
    const [gpsCoords, setGpsCoords] = useState(null);
    const [gpsStatus, setGpsStatus] = useState('loading'); // 'loading' | 'ok' | 'denied'

    const getUser = () => getAuth().currentUser;

    useEffect(() => {
        const user = getUser();
        if (!user) return;
        getDoc(doc(fs, 'users', user.uid)).then(snap => {
            if (snap.exists()) {
                setAddresses(snap.data().savedAddresses || []);
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!navigator.geolocation) { setGpsStatus('denied'); return; }
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude: lat, longitude: lng } = pos.coords;
                setGpsCoords([lat, lng]);
                try {
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
                    );
                    const data = await res.json();
                    const addr = data.address || {};
                    const house = addr.house_number;
                    const road = addr.road;
                    const city = addr.city || addr.town || addr.state;
                    const text = road && house ? `${house}, ${road}` : road && city ? `${road}, ${city}` : city || data.display_name;
                    setGpsAddress(text);
                    setGpsStatus('ok');
                } catch {
                    setGpsStatus('ok');
                }
            },
            () => setGpsStatus('denied'),
            { enableHighAccuracy: true, timeout: 8000 }
        );
    }, []);

    const persist = async (updated) => {
        const user = getUser();
        if (!user) return;
        await setDoc(doc(fs, 'users', user.uid), { savedAddresses: updated }, { merge: true }).catch(() => {});
    };

    const handleAddressSelected = (loc) => {
        setPendingAddress(loc);
        setShowAddressSheet(false);
        setLabelModal({ mode: 'new' });
    };

    const handleLabelConfirm = async (label) => {
        if (pendingAddress) {
            const newEntry = {
                id: editingId || Date.now(),
                address: pendingAddress.address,
                lat: pendingAddress.lat,
                lng: pendingAddress.lng,
                label: label || 'labelOther',
                isPrimary: addresses.length === 0,
            };
            let updated;
            if (editingId) {
                updated = addresses.map(a => a.id === editingId ? { ...a, label: newEntry.label, address: newEntry.address, lat: newEntry.lat, lng: newEntry.lng } : a);
            } else {
                updated = [newEntry, ...addresses.filter(a => a.address !== newEntry.address)].slice(0, 10);
            }
            setAddresses(updated);
            await persist(updated);
        } else if (editingId) {
            const updated = addresses.map(a => a.id === editingId ? { ...a, label } : a);
            setAddresses(updated);
            await persist(updated);
        }
        setLabelModal(null);
        setPendingAddress(null);
        setEditingId(null);
    };

    const handleDelete = async (id) => {
        const updated = addresses.filter(a => a.id !== id);
        if (updated.length > 0 && !updated.find(a => a.isPrimary)) {
            updated[0] = { ...updated[0], isPrimary: true };
        }
        setAddresses(updated);
        await persist(updated);
    };

    const handleSetPrimary = async (id) => {
        const updated = addresses.map(a => ({ ...a, isPrimary: a.id === id }));
        setAddresses(updated);
        await persist(updated);
    };

    const handleEditLabel = (item) => {
        setEditingId(item.id);
        setPendingAddress(null);
        setLabelModal({ mode: 'edit', currentLabel: item.label });
    };

    const primaryAddress = addresses.find(a => a.isPrimary) || addresses[0];
    const mapCenter = gpsCoords || (primaryAddress ? [primaryAddress.lat, primaryAddress.lng] : [41.6938, 44.8015]);
    const displayAddress = gpsAddress || primaryAddress?.address || null;

    return (
        <div className="am-overlay">
            <div className="am-header">
                <button className="am-back-btn" onClick={onClose}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 6l-6 6l6 6" />
                    </svg>
                </button>
                <span className="am-title">{t('title')}</span>
            </div>

            <div className="am-scroll">
                <div className="am-map-wrap">
                    <MapContainer
                        center={mapCenter}
                        zoom={15}
                        zoomControl={false}
                        dragging={false}
                        scrollWheelZoom={false}
                        doubleClickZoom={false}
                        touchZoom={false}
                        keyboard={false}
                        attributionControl={false}
                        style={{ width: '100%', height: '100%' }}
                    >
                        <TileLayer
                            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                        />
                        <MapFixer />
                        {gpsCoords && (
                            <Marker position={gpsCoords} icon={roundRedIcon} />
                        )}
                        {!gpsCoords && primaryAddress && (
                            <Marker position={[primaryAddress.lat, primaryAddress.lng]} icon={roundRedIcon} />
                        )}
                    </MapContainer>
                </div>

                {gpsStatus === 'denied' ? (
                    <div className="am-gps-denied">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="#f59e0b" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 9v4" /><path d="M12 17h.01" />
                            <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9-9 9-9-1.8-9-9 1.8-9 9-9z" />
                        </svg>
                        <span>{t('enableLocation')}</span>
                    </div>
                ) : (
                    <div className="am-current-location">
                        <div className="am-current-icon">
                            {gpsStatus === 'loading' ? (
                                <svg className="am-gps-spin" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2.5" stroke="#e8603c" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 3a9 9 0 1 0 9 9" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#e8603c" stroke="none">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
                                </svg>
                            )}
                        </div>
                        <div className="am-current-info">
                            <span className="am-current-label">{t('currentLocation')}</span>
                            <span className="am-current-address">{displayAddress || t('noAddress')}</span>
                        </div>
                    </div>
                )}

                <div className="am-list">
                    {addresses.map(item => {
                        const lk = normKey(item.label);
                        const color = LABEL_COLORS[lk] || '#6b7280';
                        const isHome = lk === 'labelHome';
                        return (
                            <div className="am-card" key={item.id}>
                                <div className="am-card-top">
                                    <div className="am-card-icon" style={{ background: isHome ? '#fde8e0' : '#f3f4f6', color }}>
                                        {LABEL_ICONS[lk] || LABEL_ICONS['labelOther']}
                                    </div>
                                    <div className="am-card-info">
                                        <div className="am-card-label-row">
                                            <span className="am-card-label">{t(lk)}</span>
                                            {item.isPrimary && <span className="am-primary-badge">{t('primary')}</span>}
                                        </div>
                                        <span className="am-card-address">{item.address}</span>
                                    </div>
                                </div>
                                <div className="am-card-actions">
                                    <button className="am-edit-btn" onClick={() => handleEditLabel(item)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                                            <path d="M13.5 6.5l4 4" />
                                        </svg>
                                        {t('edit')}
                                    </button>
                                    <button className="am-delete-btn" onClick={() => handleDelete(item.id)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 7l16 0" />
                                            <path d="M10 11l0 6" />
                                            <path d="M14 11l0 6" />
                                            <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                            <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                        </svg>
                                    </button>
                                </div>
                                {!item.isPrimary && (
                                    <button className="am-set-primary-btn" onClick={() => handleSetPrimary(item.id)}>
                                        {t('setPrimary')}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="am-cta">
                    <div className="am-cta-icon-wrap">
                        <div className="am-cta-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="#e8603c" stroke="none">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
                            </svg>
                            <span className="am-cta-plus">+</span>
                        </div>
                    </div>
                    <div className="am-cta-text">{t('ctaTitle')}</div>
                    <div className="am-cta-sub">{t('ctaSub')}</div>
                    <button className="am-add-btn" onClick={() => setShowAddressSheet(true)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5l0 14" />
                            <path d="M5 12l14 0" />
                        </svg>
                        {t('addNew')}
                    </button>
                </div>

                <div className="am-bottom-space" />
            </div>

            {showAddressSheet && (
                <AddressSheet
                    onClose={() => setShowAddressSheet(false)}
                    onSelectAddress={handleAddressSelected}
                    currentAddress={primaryAddress?.address}
                />
            )}

            {labelModal && (
                <div className="am-label-overlay" onClick={() => { setLabelModal(null); setPendingAddress(null); setEditingId(null); }}>
                    <div className="am-label-modal" onClick={e => e.stopPropagation()}>
                        <button className="am-label-close" onClick={() => { setLabelModal(null); setPendingAddress(null); setEditingId(null); }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 6l-12 12" /><path d="M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="am-label-title">{t('labelModalTitle')}</div>
                        <div className="am-label-grid">
                            {LABEL_KEYS.map(lk => {
                                const isActive = normKey(labelModal.currentLabel) === lk;
                                const isHomeType = lk === 'labelHome';
                                return (
                                    <button
                                        key={lk}
                                        className={`am-label-option ${isActive ? 'am-label-option--active' : ''}`}
                                        onClick={() => handleLabelConfirm(lk)}
                                    >
                                        <span className="am-label-opt-icon" style={{ background: isHomeType ? '#fde8e0' : '#f3f4f6', color: isHomeType ? '#e8603c' : '#6b7280' }}>
                                            {LABEL_ICONS[lk]}
                                        </span>
                                        <span>{t(lk)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddressManager;
